import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../lib/apiClient";
import { Bell } from "lucide-react";

// Notification bell with unseen badge + dropdown. Surfaces watchlist price drops.
export function NotificationBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const unseen = items.filter((n) => !n.seen).length;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
    } catch (_) {}
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [load]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unseen > 0) {
      try {
        await api.post("/notifications/mark-seen");
        setItems((prev) => prev.map((n) => ({ ...n, seen: true })));
      } catch (_) {}
    }
  };

  const label = (n) => {
    if (n.name || n.type === "price_drop")
      return `${n.name || "Produit"} — ${n.current_price_eur ?? "?"} € (cible ${n.target_price_eur ?? "?"} €)`;
    return n.message || "Notification";
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notif-bell"
        onClick={toggle}
        aria-label="Notifications"
        title="Notifications"
        className="hud-btn h-9 w-9 flex items-center justify-center clip-corner relative"
      >
        <Bell className="h-4 w-4" />
        {unseen > 0 && (
          <span
            data-testid="notif-badge"
            className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-[#ff5470] text-white text-[9px] font-bold flex items-center justify-center"
          >
            {unseen > 9 ? "9+" : unseen}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid="notif-panel"
          className="absolute right-0 mt-2 w-80 max-w-[90vw] clip-corner border border-[var(--border-hud)] bg-[rgba(4,10,22,0.97)] backdrop-blur-md shadow-[0_10px_40px_rgba(0,0,0,0.5)] z-[90]"
        >
          <div className="px-3 py-2 border-b border-[var(--border-hud)] font-mono-hud text-[10px] tracking-[0.2em] text-[#8b949e] uppercase">
            Notifications
          </div>
          {items.length === 0 ? (
            <div className="px-3 py-6 text-center text-[11px] text-[#6b7a8d]" data-testid="notif-empty">
              Aucune notification
            </div>
          ) : (
            <ul className="max-h-80 overflow-y-auto">
              {items.map((n) => (
                <li
                  key={n.id}
                  data-testid={`notif-item-${n.id}`}
                  onClick={() => setOpen(false)}
                  className="px-3 py-2.5 border-b border-[var(--border-hud)] flex items-start gap-2 hover:bg-[var(--accent-a10)] cursor-pointer last:border-b-0"
                >
                  <span
                    className="mt-1 h-1.5 w-1.5 rounded-full shrink-0"
                    style={{ background: "#00ffaa" }}
                  />
                  <div className="min-w-0">
                    <p className="text-[11.5px] text-white/90 leading-snug">{label(n)}</p>
                    <p className="text-[9px] text-[#6b7a8d] mt-0.5 font-mono-hud">
                      {n.created_at ? new Date(n.created_at).toLocaleString("fr-FR") : ""}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
